import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Avatar, Box, Typography, Button, Chip, Tabs, Tab, Paper, Stack, TextField,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, FormControlLabel, Switch, CircularProgress,
  Autocomplete, MenuItem,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { TableKit, toHebrewNumeral } from '@NP-Webber/crm-kit';
import { Link as RouterLink } from 'react-router-dom';
import api from '../../services/api.js';
import { downloadInboxMessage, downloadInboxZip, formatBytes } from '../../next/inboxDownload.js';
import {
  IS_MISHTAMERET,
  LESSON_CHANNELS,
  contentLessonChannels,
} from '../../data/lessonChannels.js';
import {
  TRACTATE_NAMES,
  getMaxDaf,
  formatLessonPageRefDisplay,
} from '../../data/gemaraTractates.js';
import { formatHebrewDate, formatHebrewDateTime } from '../../utils/hebrewDate';
import AudioPlayer from '../../components/AudioPlayer.jsx';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';
import { ckConfirm } from '@NP-Webber/crm-kit/dist/next/index.js';

function yemotProxyPath(filePath) {
  return String(filePath || '')
    .replace(/^ivr2:/i, '')
    .replace(/^\//, '')
    .trim();
}

function sourceLabel(source) {
  if (source === 'phone_ivr') return 'טלפון';
  if (source === 'import') return 'ייבוא';
  return 'העלאה';
}

const MESSAGE_COLUMNS = [
  { key: 'title', title: 'כותרת', sortable: true, filterable: true, minWidth: '160px' },
  {
    key: 'type', title: 'סוג', sortable: true, filterable: true,
    render: (v) => (v === 'general' ? 'כללית' : 'אישית'),
  },
  { key: 'priority', title: 'עדיפות', sortable: true, minWidth: '70px' },
  {
    key: 'status', title: 'סטטוס', sortable: true, filterable: true,
    render: (v) => {
      const map = {
        draft: { icon: <EditNoteIcon sx={{ fontSize: 16 }} />, label: 'טיוטה', color: 'default' },
        active: { icon: <PlayCircleOutlineIcon sx={{ fontSize: 16 }} />, label: 'פעילה', color: 'primary' },
        paused: { icon: <PauseCircleOutlineIcon sx={{ fontSize: 16 }} />, label: 'מושהית', color: 'warning' },
      };
      const entry = map[v];
      if (!entry) return v;
      return <Chip icon={entry.icon} label={entry.label} size="small" color={entry.color} variant="outlined" />;
    },
  },
  {
    key: 'validFrom', title: 'מ-', sortable: true,
    render: (v) => formatHebrewDate(v),
  },
  {
    key: 'validTo', title: 'עד', sortable: true,
    render: (v) => formatHebrewDate(v),
  },
];

const emptyDrashaForm = () => ({
  title: '',
  speaker: '',
  date: new Date().toISOString().slice(0, 10),
  category: '',
  status: 'inactive',
  announceDetails: true,
  tractate: '',
  daf: '',
  amud: '',
  announcePageRef: false,
});

const DEFAULT_CHANNEL = 'drasha';

function clearPageRefInForm(form) {
  return { ...form, tractate: '', daf: '', amud: '', announcePageRef: false };
}

function MessagesTab() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchMessages = useCallback(async ({ page, pageSize, sort, order, filters }) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize,
        sort: sort || 'createdAt',
        order: order || 'desc',
        status: filters?.status || '',
        type: filters?.type || '',
        excludeInbox: 1,
      };
      const { data } = await api.get('/messages', { params });
      setMessages(data.data);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleExportFetch = useCallback(async ({ sort, order, filters }) => {
    const params = {
      all: 1,
      sort: sort || 'createdAt',
      order: order || 'desc',
      status: filters?.status || '',
      type: filters?.type || '',
      excludeInbox: 1,
    };
    const { data } = await api.get('/messages', { params });
    return data.data;
  }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>הודעות</Typography>
        <Button variant="contained" color="secondary">+ הודעה חדשה</Button>
      </Box>
      <TableKit
        columns={MESSAGE_COLUMNS}
        data={messages}
        total={total}
        loading={loading}
        onFetch={fetchMessages}
        onExportFetch={handleExportFetch}
        clientSideMode={false}
        emptyMessage="אין הודעות"
        exportFileName="הודעות"
      />
    </Box>
  );
}

function RecordingDownloadButton({ id }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const onClick = async () => {
    setBusy(true);
    setError('');
    try {
      await downloadInboxMessage(id);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <IconButton size="small" aria-label="הורדת ההקלטה" title={error || 'הורדת ההקלטה'} color={error ? 'error' : 'default'} disabled={busy} onClick={onClick}>
      {busy ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
    </IconButton>
  );
}

const SYSTEM_MESSAGE_COLUMNS = [
  {
    key: 'createdAt', title: 'תאריך', sortable: true, minWidth: '150px',
    render: (v) => (v ? formatHebrewDateTime(v) : '—'),
  },
  {
    key: 'member', title: 'חבר', sortable: false, minWidth: '140px',
    render: (v, row) => {
      // TableKit: ערך תא או שורה שלמה
      const m = row && row.createdBy !== undefined ? row.member : (v && v.createdBy !== undefined ? v.member : v);
      const name = `${m?.firstName || ''} ${m?.lastName || ''}`.trim();
      if (!m?._id) return <Typography variant="body2" color="text.secondary">לא מזוהה</Typography>;
      return (
        <Typography component={RouterLink} to={`/members/${m._id}`} variant="body2" fontWeight={600} color="primary" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          {name || 'ללא שם'}
        </Typography>
      );
    },
  },
  {
    key: 'createdBy', title: 'טלפון מתקשר', sortable: true, minWidth: '130px',
    render: (v) => {
      const raw = String(v || '');
      const phone = raw.startsWith('ivr-') ? raw.slice(4) : raw;
      return phone || '—';
    },
  },
  {
    key: 'title', title: 'סוג', sortable: true, minWidth: '110px',
    render: (v) => (String(v || '').includes('הנהלה') ? 'להנהלה' : 'למערכת'),
  },
  {
    key: 'audioUrl', title: 'הקלטה', sortable: false, minWidth: '120px',
    render: (v, row) => {
      // TableKit: ערך תא או שורה שלמה
      const rec = (row && row.audioUrl !== undefined)
        ? row
        : (v && typeof v === 'object' && v.audioUrl !== undefined ? v : null);
      const audioUrl = rec ? rec.audioUrl : v;
      const body = String(rec?.body || '');
      const fromSource = body.startsWith('yemot-source:')
        ? body.slice('yemot-source:'.length)
        : '';
      const path = yemotProxyPath(fromSource || audioUrl);
      return path && rec?._id
        ? (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <AudioPlayer
              src={`/messages/${rec._id}/audio`}
              label="הודעה למערכת"
            />
            <RecordingDownloadButton id={rec._id} />
          </Stack>
        )
        : '—';
    },
  },
  {
    key: 'downloadedAt', title: 'הורד', sortable: true, minWidth: '140px',
    render: (v) => (v ? formatHebrewDateTime(v) : 'טרם הורד'),
  },
];

function SystemMessagesTab() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [notDownloaded, setNotDownloaded] = useState(0);
  const [downloading, setDownloading] = useState(null); // { bytes } while a ZIP is being prepared
  const [downloadMsg, setDownloadMsg] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const fetchMessages = useCallback(async ({ page, pageSize, sort, order }) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize,
        sort: sort || 'createdAt',
        order: order || 'desc',
        inbox: 'ivr',
      };
      const { data } = await api.get('/messages', { params });
      setMessages(data.data);
      setTotal(data.total);
      setNotDownloaded(data.notDownloadedCount || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const download = async (onlyNew) => {
    if (downloading) return;
    setDownloading({ bytes: 0 });
    try {
      const { fileCount } = await downloadInboxZip({ onlyNew, onProgress: (bytes) => setDownloading({ bytes }) });
      setDownloadMsg({ severity: 'success', text: fileCount ? `הורדו ${fileCount} הקלטות` : 'הקובץ הורד' });
      setReloadTick((n) => n + 1);
    } catch (e) {
      setDownloadMsg({ severity: 'error', text: e.message });
    } finally {
      setDownloading(null);
    }
  };

  const handleExportFetch = useCallback(async ({ sort, order }) => {
    const params = {
      all: 1,
      sort: sort || 'createdAt',
      order: order || 'desc',
      inbox: 'ivr',
    };
    const { data } = await api.get('/messages', { params });
    return data.data;
  }, []);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" fontWeight={600} sx={{ flexGrow: 1 }}>הודעות למערכת</Typography>
        {downloading ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={18} />
            <Typography variant="body2">{`מכין ZIP…${downloading.bytes ? ` ${formatBytes(downloading.bytes)}` : ''}`}</Typography>
          </Stack>
        ) : null}
        <Button variant="contained" size="small" disabled={!!downloading || !notDownloaded} onClick={() => download(true)}>
          {notDownloaded ? `הורדת מה שטרם הורד (${notDownloaded})` : 'הכול כבר הורד'}
        </Button>
        <Button variant="outlined" size="small" disabled={!!downloading || !total} onClick={() => download(false)}>
          הורדת כל ההקלטות
        </Button>
      </Stack>
      <TableKit
        key={reloadTick}
        columns={SYSTEM_MESSAGE_COLUMNS}
        data={messages}
        total={total}
        loading={loading}
        onFetch={fetchMessages}
        onExportFetch={handleExportFetch}
        clientSideMode={false}
        emptyMessage="אין הודעות למערכת"
        exportFileName="הודעות למערכת"
      />
      <Snackbar open={!!downloadMsg} autoHideDuration={5000} onClose={() => setDownloadMsg(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {downloadMsg ? <Alert severity={downloadMsg.severity} onClose={() => setDownloadMsg(null)}>{downloadMsg.text}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
}

function DrashasTab({
  channel = DEFAULT_CHANNEL,
  title = 'דרשות / שמועס׳ן',
  subtitle = 'מופיעות בשלוחה 5 בטלפון. חזרה למקום נשמרת בימות אחרי העלאת אודיו.',
  itemLabel = 'דרשה',
}) {
  const [drashas, setDrashas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyDrashaForm());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [channelSettings, setChannelSettings] = useState({
    announceDetails: true,
    announcePageRef: false,
    hasProfileImage: false,
  });
  const [savingChannelSettings, setSavingChannelSettings] = useState(false);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState('');
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const fileInputRef = useRef(null);
  const profileImageInputRef = useRef(null);
  const uploadTargetIdRef = useRef(null);
  const profilePreviewUrlRef = useRef('');

  const revokeProfilePreview = useCallback(() => {
    if (profilePreviewUrlRef.current) {
      URL.revokeObjectURL(profilePreviewUrlRef.current);
      profilePreviewUrlRef.current = '';
    }
    setProfilePreviewUrl('');
  }, []);

  const loadProfilePreview = useCallback(async () => {
    revokeProfilePreview();
    try {
      const { data } = await api.get(
        `/drashas/channel-settings/${encodeURIComponent(channel)}/profile-image`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(data);
      profilePreviewUrlRef.current = url;
      setProfilePreviewUrl(url);
    } catch {
      revokeProfilePreview();
    }
  }, [channel, revokeProfilePreview]);

  const loadChannelSettings = useCallback(async () => {
    try {
      const { data } = await api.get(`/drashas/channel-settings/${encodeURIComponent(channel)}`);
      const hasProfileImage = data.hasProfileImage === true;
      setChannelSettings({
        announceDetails: data.announceDetails !== false,
        announcePageRef: data.announcePageRef === true,
        hasProfileImage,
      });
      if (hasProfileImage) await loadProfilePreview();
      else revokeProfilePreview();
    } catch (err) {
      console.error(err);
    }
  }, [channel, loadProfilePreview, revokeProfilePreview]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/drashas', {
        params: { page: 1, pageSize: 100, channel },
      });
      setDrashas(data.data || []);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'שגיאה בטעינת תוכן', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [channel]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadChannelSettings(); }, [loadChannelSettings]);
  useEffect(() => () => {
    if (profilePreviewUrlRef.current) URL.revokeObjectURL(profilePreviewUrlRef.current);
  }, []);

  const saveChannelSettings = async (patch) => {
    const next = { ...channelSettings, ...patch };
    setChannelSettings(next);
    setSavingChannelSettings(true);
    try {
      await api.put(`/drashas/channel-settings/${encodeURIComponent(channel)}`, {
        announceDetails: next.announceDetails,
        announcePageRef: next.announcePageRef,
      });
      setSnackbar({
        open: true,
        message: 'הגדרות הערוץ עודכנו וחלו על כל השיעורים',
        severity: 'success',
      });
      await loadData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'שגיאה בשמירת הגדרות הערוץ',
        severity: 'error',
      });
      await loadChannelSettings();
    } finally {
      setSavingChannelSettings(false);
    }
  };

  const handleProfileImageSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    setUploadingProfile(true);
    try {
      const { data } = await api.post(
        `/drashas/channel-settings/${encodeURIComponent(channel)}/profile-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setChannelSettings((prev) => ({
        ...prev,
        hasProfileImage: data.hasProfileImage !== false,
      }));
      await loadProfilePreview();
      setSnackbar({ open: true, message: 'תמונת הפרופיל נשמרה לערוץ', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'שגיאה בהעלאת תמונת פרופיל',
        severity: 'error',
      });
    } finally {
      setUploadingProfile(false);
    }
  };

  const handleRemoveProfileImage = async () => {
    setUploadingProfile(true);
    try {
      await api.delete(`/drashas/channel-settings/${encodeURIComponent(channel)}/profile-image`);
      setChannelSettings((prev) => ({ ...prev, hasProfileImage: false }));
      revokeProfilePreview();
      setSnackbar({ open: true, message: 'תמונת הפרופיל הוסרה', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'שגיאה בהסרת תמונת פרופיל',
        severity: 'error',
      });
    } finally {
      setUploadingProfile(false);
    }
  };

  const openCreate = () => {
    setEditId(null);
    setForm({
      ...emptyDrashaForm(),
      announceDetails: channelSettings.announceDetails !== false,
      announcePageRef: channelSettings.announcePageRef === true,
    });
    setDialogOpen(true);
  };

  const openEdit = (d) => {
    setEditId(d._id);
    setForm({
      title: d.title || '',
      speaker: d.speaker || '',
      date: d.date ? new Date(d.date).toISOString().slice(0, 10) : '',
      category: d.category || '',
      status: d.status === 'active' ? 'active' : 'inactive',
      announceDetails: d.announceDetails !== false,
      tractate: d.tractate || '',
      daf: d.daf != null && d.daf !== '' ? String(d.daf) : '',
      amud: d.amud === 'a' || d.amud === 'b' ? d.amud : '',
      announcePageRef: d.announcePageRef === true,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.date) {
      setSnackbar({ open: true, message: 'תאריך חובה', severity: 'error' });
      return;
    }

    const tractate = String(form.tractate || '').trim();
    const dafRaw = String(form.daf || '').trim();
    const amud = form.amud === 'a' || form.amud === 'b' ? form.amud : '';
    const anyPage = !!(tractate || dafRaw || amud);
    if (anyPage && (!tractate || !dafRaw)) {
      setSnackbar({
        open: true,
        message: 'שיוך דף דורש מסכת ומספר דף',
        severity: 'error',
      });
      return;
    }
    if (anyPage) {
      const dafNum = Number(dafRaw);
      const max = getMaxDaf(tractate);
      if (!Number.isInteger(dafNum) || dafNum < 2 || (max != null && dafNum > max)) {
        setSnackbar({
          open: true,
          message: max != null ? `מספר דף חייב להיות בין 2 ל-${max}` : 'מספר דף לא תקין',
          severity: 'error',
        });
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        title: String(form.title || '').trim(),
        speaker: String(form.speaker || '').trim(),
        date: form.date,
        category: String(form.category || '').trim(),
        channel,
        status: form.status,
        announceDetails: form.announceDetails !== false,
        tractate: anyPage ? tractate : '',
        daf: anyPage ? Number(dafRaw) : null,
        amud: anyPage ? amud : '',
        announcePageRef: anyPage ? form.announcePageRef === true : false,
      };

      if (editId) {
        await api.put(`/drashas/${editId}`, payload);
        setSnackbar({ open: true, message: `${itemLabel} עודכן/ה`, severity: 'success' });
      } else {
        const { data: created } = await api.post('/drashas', {
          ...payload,
          status: 'inactive',
        });
        setSnackbar({
          open: true,
          message: `${itemLabel} נוצר/ה — העלו קובץ שמע ואז הפעילו`,
          severity: 'success',
        });
        setDialogOpen(false);
        await loadData();
        // פתיחת בחירת קובץ מיד אחרי יצירה
        uploadTargetIdRef.current = created._id;
        fileInputRef.current?.click();
        return;
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'שגיאה',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/drashas/${deleteTarget._id}`);
      setSnackbar({ open: true, message: `${itemLabel} נמחק/ה`, severity: 'success' });
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'שגיאה',
        severity: 'error',
      });
      setDeleteTarget(null);
    }
  };

  const triggerUpload = (id) => {
    uploadTargetIdRef.current = id;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const id = uploadTargetIdRef.current;
    e.target.value = '';
    if (!file || !id) return;

    const formData = new FormData();
    formData.append('audio', file);
    setUploadingId(id);
    try {
      const { data: res } = await api.post(`/drashas/${id}/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.yemotSynced) {
        setSnackbar({ open: true, message: 'האודיו הועלה וסונכרן לימות', severity: 'success' });
      } else {
        setSnackbar({
          open: true,
          message: 'הקובץ נשמר, אך הסנכרון לימות נכשל — בדקו טוקן ימות',
          severity: 'warning',
        });
      }
      loadData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'שגיאה בהעלאת אודיו',
        severity: 'error',
      });
    } finally {
      setUploadingId(null);
      uploadTargetIdRef.current = null;
    }
  };

  const toggleActive = async (d) => {
    const next = d.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/drashas/${d._id}`, { status: next });
      loadData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'שגיאה',
        severity: 'error',
      });
    }
  };

  const moveDrasha = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= drashas.length || reordering) return;

    const prev = drashas;
    const next = [...drashas];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    setDrashas(next);
    setReordering(true);
    try {
      await api.put('/drashas/reorder', { ids: next.map((d) => d._id), channel });
    } catch (err) {
      setDrashas(prev);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'שגיאה בשינוי הסדר',
        severity: 'error',
      });
      loadData();
    } finally {
      setReordering(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        hidden
        onChange={handleFileSelected}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
            {' '}הסדר כאן הוא סדר התפריט בטלפון.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          {itemLabel} חדש/ה
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          הגדרות השמעה לכל הערוץ
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          שינוי כאן חל מיד על כל השיעורים בערוץ, ומשמש כברירת מחדל לשיעורים חדשים. ניתן לשנות פרטנית בכל שיעור.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={channelSettings.announceDetails !== false}
                disabled={savingChannelSettings}
                onChange={(e) => saveChannelSettings({ announceDetails: e.target.checked })}
              />
            }
            label="השמע כותרת ומרצה בטלפון (לכל הערוץ)"
          />
          {IS_MISHTAMERET && (
            <FormControlLabel
              control={
                <Switch
                  checked={channelSettings.announcePageRef === true}
                  disabled={savingChannelSettings}
                  onChange={(e) => saveChannelSettings({ announcePageRef: e.target.checked })}
                />
              }
              label="השמע שיוך דף בטלפון (לכל הערוץ)"
            />
          )}
        </Stack>
        {IS_MISHTAMERET && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ sm: 'center' }}
            sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}
          >
            <Avatar
              src={profilePreviewUrl || undefined}
              sx={{ width: 56, height: 56, bgcolor: 'action.hover', color: 'text.secondary' }}
            >
              <PersonIcon />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>
                תמונת פרופיל לערוץ
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                מוצגת בפורטל המשתמרת ליד כל שיעור בערוץ זה
              </Typography>
            </Box>
            <input
              ref={profileImageInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              hidden
              onChange={handleProfileImageSelected}
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={uploadingProfile ? <CircularProgress size={16} /> : <UploadFileIcon />}
                disabled={uploadingProfile}
                onClick={() => profileImageInputRef.current?.click()}
              >
                {channelSettings.hasProfileImage ? 'החלפה' : 'העלאה'}
              </Button>
              {channelSettings.hasProfileImage ? (
                <Button
                  size="small"
                  color="error"
                  variant="text"
                  disabled={uploadingProfile}
                  onClick={handleRemoveProfileImage}
                >
                  הסרה
                </Button>
              ) : null}
            </Stack>
          </Stack>
        )}
      </Paper>

      <Stack spacing={1}>
        {drashas.map((d, index) => (
          <Paper key={d._id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              <IconButton
                size="small"
                onClick={() => moveDrasha(index, -1)}
                disabled={reordering || index === 0}
                aria-label="העלאה בסדר"
                title="העלאה בסדר"
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => moveDrasha(index, 1)}
                disabled={reordering || index === drashas.length - 1}
                aria-label="הורדה בסדר"
                title="הורדה בסדר"
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={600} noWrap>
                {d.title?.trim() ? d.title : 'ללא כותרת'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {[
                  d.speaker?.trim() || null,
                  d.date ? formatHebrewDate(d.date) : null,
                  d.category || null,
                  d.announceDetails === false ? 'ללא השמעת פרטים בטלפון' : null,
                ].filter(Boolean).join(' · ')}
              </Typography>
              {IS_MISHTAMERET && formatLessonPageRefDisplay(d, toHebrewNumeral) && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {formatLessonPageRefDisplay(d, toHebrewNumeral)}
                  {d.announcePageRef === true ? ' · מושמע בטלפון' : ''}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {d.yemotSynced && (d.yemotFilePath || d.audioUrl)
                  ? `ימות: ${d.yemotFilePath || d.audioUrl}${d.audioFilename ? ` (${d.audioFilename})` : ''}`
                  : 'אין אודיו מסונכרן לימות'}
              </Typography>
            </Box>
            {d.source && d.source !== 'admin_upload' && (
              <Chip
                label={sourceLabel(d.source)}
                size="small"
                color={d.source === 'phone_ivr' ? 'info' : 'default'}
                variant="outlined"
              />
            )}
            {(yemotProxyPath(d.yemotFilePath) || (d.yemotSynced && d.audioUrl)) ? (
              <AudioPlayer src={`/drashas/${d._id}/audio`} label={d.title || 'שיעור'} />
            ) : null}
            <Chip
              label={d.status === 'active' ? 'פעילה' : 'כבויה'}
              size="small"
              color={d.status === 'active' ? 'success' : 'default'}
              onClick={() => toggleActive(d)}
              sx={{ cursor: 'pointer' }}
            />
            <IconButton
              size="small"
              onClick={() => triggerUpload(d._id)}
              disabled={uploadingId === d._id}
              aria-label="העלאת אודיו"
              title="העלאת אודיו"
            >
              {uploadingId === d._id
                ? <CircularProgress size={18} />
                : <UploadFileIcon fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={() => openEdit(d)} aria-label="עריכה">
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(d)} aria-label="מחיקה">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Paper>
        ))}
        {!drashas.length && (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">אין פריטים — לחצו על «{itemLabel} חדש/ה»</Typography>
          </Paper>
        )}
      </Stack>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? `עריכת ${itemLabel}` : `${itemLabel} חדש/ה`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="כותרת (אופציונלי)"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              label="מרצה (אופציונלי)"
              value={form.speaker}
              onChange={(e) => setForm((f) => ({ ...f, speaker: e.target.value }))}
              fullWidth
            />
            <TextField
              label="תאריך"
              type="date"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="קטגוריה (אופציונלי)"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              fullWidth
            />
            {IS_MISHTAMERET && (
              <>
                <Typography variant="subtitle2" fontWeight={600}>
                  שיוך לדף / עמוד (אופציונלי)
                </Typography>
                <Autocomplete
                  options={TRACTATE_NAMES}
                  value={form.tractate || null}
                  onChange={(_e, value) => setForm((f) => ({ ...f, tractate: value || '' }))}
                  renderInput={(params) => (
                    <TextField {...params} label="מסכת" fullWidth />
                  )}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="דף"
                    type="number"
                    value={form.daf}
                    onChange={(e) => setForm((f) => ({ ...f, daf: e.target.value }))}
                    fullWidth
                    inputProps={{ min: 2, max: getMaxDaf(form.tractate) || undefined }}
                    helperText={
                      form.tractate && getMaxDaf(form.tractate)
                        ? `מקסימום: ${getMaxDaf(form.tractate)} (${toHebrewNumeral(getMaxDaf(form.tractate))})`
                        : 'מינימום דף 2'
                    }
                  />
                  <TextField
                    select
                    label="עמוד"
                    value={form.amud}
                    onChange={(e) => setForm((f) => ({ ...f, amud: e.target.value }))}
                    fullWidth
                  >
                    <MenuItem value="">כל הדף</MenuItem>
                    <MenuItem value="a">א</MenuItem>
                    <MenuItem value="b">ב</MenuItem>
                  </TextField>
                </Stack>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.announcePageRef === true}
                        disabled={!form.tractate && !form.daf && !form.amud}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          announcePageRef: e.target.checked,
                        }))}
                      />
                    }
                    label="השמע שיוך דף בטלפון"
                  />
                  <Button
                    size="small"
                    onClick={() => setForm((f) => clearPageRefInForm(f))}
                    disabled={!form.tractate && !form.daf && !form.amud}
                  >
                    נקה שיוך
                  </Button>
                </Box>
              </>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={form.announceDetails !== false}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    announceDetails: e.target.checked,
                  }))}
                />
              }
              label="השמע כותרת ומרצה בטלפון"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              כבו את המתג אם הפרטים כבר מושמעים בהקלטה עצמה
            </Typography>
            {editId && (
              <FormControlLabel
                control={
                  <Switch
                    checked={form.status === 'active'}
                    onChange={(e) => setForm((f) => ({
                      ...f,
                      status: e.target.checked ? 'active' : 'inactive',
                    }))}
                  />
                }
                label="פעילה (רק אחרי סנכרון אודיו לימות)"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'שומר...' : 'שמירה'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>מחיקת {itemLabel}</DialogTitle>
        <DialogContent>
          <Typography>
            למחוק את «{deleteTarget?.title?.trim() || 'ללא כותרת'}»? הקובץ יימחק גם מימות אם קיים.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>ביטול</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>מחיקה</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function ShinunMagashTab() {
  const [magidim, setMagidim] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', menuKey: '', status: 'active' });
  const [saving, setSaving] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadMagidim = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/shinun-magidim', { params: { all: '1' } });
      const rows = data.data || [];
      setMagidim(rows);
      setSelectedChannel((prev) => {
        if (prev && rows.some((m) => m.channel === prev && m.status === 'active')) return prev;
        const firstActive = rows.find((m) => m.status === 'active');
        return firstActive?.channel || '';
      });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'שגיאה בטעינת מגידים', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMagidim(); }, [loadMagidim]);

  const usedKeys = new Set(
    magidim.filter((m) => m.status === 'active' && m.id !== editId).map((m) => m.menuKey)
  );
  const availableKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9'].filter(
    (k) => !usedKeys.has(k) || form.menuKey === k
  );

  const openCreate = () => {
    setEditId(null);
    const nextKey = ['1', '2', '3', '4', '5', '6', '7', '8', '9'].find((k) => !usedKeys.has(k)) || '1';
    setForm({ name: '', menuKey: nextKey, status: 'active' });
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditId(m.id);
    setForm({ name: m.name || '', menuKey: m.menuKey || '', status: m.status || 'active' });
    setDialogOpen(true);
  };

  const save = async () => {
    const name = String(form.name || '').trim();
    if (!name) {
      setSnackbar({ open: true, message: 'שם מגיד נדרש', severity: 'error' });
      return;
    }
    if (!/^[1-9]$/.test(String(form.menuKey))) {
      setSnackbar({ open: true, message: 'מקש חייב להיות 1–9', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/shinun-magidim/${editId}`, {
          name,
          menuKey: form.menuKey,
          status: form.status,
        });
      } else {
        await api.post('/shinun-magidim', {
          name,
          menuKey: form.menuKey,
          status: form.status,
        });
      }
      setDialogOpen(false);
      setSnackbar({ open: true, message: 'נשמר', severity: 'success' });
      await loadMagidim();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'שגיאה בשמירה',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (m) => {
    if (!(await ckConfirm({ title: 'השבתת מגיד', message: `להשבית את המגיד «${m.name}»? השיעורים יישארו במערכת.`, confirmLabel: 'השבתה' }))) return;
    try {
      await api.delete(`/shinun-magidim/${m.id}`);
      setSnackbar({ open: true, message: 'המגיד הושבת', severity: 'success' });
      await loadMagidim();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'שגיאה',
        severity: 'error',
      });
    }
  };

  const activeMagidim = magidim.filter((m) => m.status === 'active');
  const selected = activeMagidim.find((m) => m.channel === selectedChannel);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Box flex={1}>
          <Typography variant="h6" fontWeight={700}>מג״ש בחצר קדשינו</Typography>
          <Typography variant="body2" color="text.secondary">
            פתחו מגיד חדש — יופיע כתת־שלוחה בטלפון (2←1←5) ובפורטל. לכל מגיד העלו שיעורים בנפרד.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          מגיד חדש
        </Button>
      </Stack>

      {loading ? (
        <CircularProgress size={28} />
      ) : (
        <Paper variant="outlined" sx={{ mb: 2, p: 1.5 }}>
          {magidim.length === 0 ? (
            <Typography color="text.secondary">עדיין אין מגידים — לחצו «מגיד חדש».</Typography>
          ) : (
            <Stack spacing={1}>
              {magidim.map((m) => (
                <Stack
                  key={m.id}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: m.channel === selectedChannel ? 'action.selected' : 'transparent',
                    opacity: m.status === 'active' ? 1 : 0.55,
                  }}
                >
                  <Chip size="small" label={`מקש ${m.menuKey}`} />
                  <Typography flex={1} fontWeight={600}>{m.name}</Typography>
                  <Chip
                    size="small"
                    label={m.status === 'active' ? 'פעיל' : 'מושבת'}
                    color={m.status === 'active' ? 'success' : 'default'}
                  />
                  {m.status === 'active' && (
                    <Button size="small" onClick={() => setSelectedChannel(m.channel)}>
                      שיעורים
                    </Button>
                  )}
                  <IconButton size="small" onClick={() => openEdit(m)} aria-label="עריכה">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {m.status === 'active' && (
                    <IconButton size="small" color="error" onClick={() => deactivate(m)} aria-label="השבתה">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </Paper>
      )}

      {selected && (
        <DrashasTab
          channel={selected.channel}
          title={selected.name}
          subtitle={`ערוץ מג״ש — שלוחה 77/2/1/5 מקש ${selected.menuKey}.`}
          itemLabel="שיעור"
        />
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editId ? 'עריכת מגיד' : 'מגיד חדש'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="שם המגיד"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              select
              label="מקש בטלפון"
              value={form.menuKey}
              onChange={(e) => setForm((f) => ({ ...f, menuKey: e.target.value }))}
              fullWidth
            >
              {availableKeys.map((k) => (
                <MenuItem key={k} value={k}>{k}</MenuItem>
              ))}
            </TextField>
            {editId && (
              <TextField
                select
                label="סטטוס"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                fullWidth
              >
                <MenuItem value="active">פעיל</MenuItem>
                <MenuItem value="inactive">מושבת</MenuItem>
              </TextField>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>ביטול</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'שומר...' : 'שמירה'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function LessonRecordersTab() {
  const recorderChannels = contentLessonChannels();
  const emptyForm = () => ({
    name: '',
    pin: '',
    channel: recorderChannels[0]?.id || '',
    notes: '',
    active: true,
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const channelLabel = (id) => recorderChannels.find((c) => c.id === id)?.label
    || LESSON_CHANNELS.find((c) => c.id === id)?.label
    || id;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/lesson-recorders');
      setRows(data.data || []);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'שגיאה בטעינת דרשנים', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditId(r._id || r.id);
    setForm({
      name: r.name || '',
      pin: r.pin || '',
      channel: r.channel || recorderChannels[0]?.id || '',
      notes: r.notes || '',
      active: r.active !== false,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    const name = String(form.name || '').trim();
    const pin = String(form.pin || '').replace(/\D/g, '');
    if (!name) {
      setSnackbar({ open: true, message: 'שם נדרש', severity: 'error' });
      return;
    }
    if (pin.length < 4 || pin.length > 8) {
      setSnackbar({ open: true, message: 'PIN חייב להיות 4–8 ספרות', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name,
        pin,
        channel: form.channel,
        notes: form.notes,
        active: form.active,
      };
      if (editId) {
        await api.patch(`/lesson-recorders/${editId}`, payload);
      } else {
        await api.post('/lesson-recorders', payload);
      }
      setDialogOpen(false);
      setSnackbar({ open: true, message: 'נשמר', severity: 'success' });
      await loadData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'שגיאה בשמירה',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (r) => {
    const id = r._id || r.id;
    try {
      await api.delete(`/lesson-recorders/${id}`);
      setSnackbar({ open: true, message: 'הושבת', severity: 'success' });
      await loadData();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'שגיאה בהשבתה',
        severity: 'error',
      });
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>דרשנים — הקלטה בטלפון</Typography>
          <Typography variant="body2" color="text.secondary">
            כניסה: שלוחה 77 ← 7 ← 3 (או /77/record). אחרי אישור הקובץ נכנס לתיקיית playfile לשמיעה.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          דרשן חדש
        </Button>
      </Stack>

      {loading ? (
        <CircularProgress size={28} />
      ) : (
        <Stack spacing={1}>
          {rows.map((r) => (
            <Paper key={r._id || r.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={600}>{r.name}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  PIN: {r.pin} · {channelLabel(r.channel)}
                  {r.listenFolder ? ` · שמיעה: ${r.listenFolder}` : ''}
                </Typography>
                {r.notes ? (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {r.notes}
                  </Typography>
                ) : null}
              </Box>
              <Chip
                label={r.active !== false ? 'פעיל' : 'כבוי'}
                size="small"
                color={r.active !== false ? 'success' : 'default'}
              />
              <IconButton size="small" onClick={() => openEdit(r)} aria-label="עריכה">
                <EditIcon fontSize="small" />
              </IconButton>
              {r.active !== false && (
                <IconButton size="small" color="error" onClick={() => deactivate(r)} aria-label="השבתה">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Paper>
          ))}
          {!rows.length && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">אין דרשנים — הוסיפו דרשן עם קוד PIN וערוץ שמיעה</Typography>
            </Paper>
          )}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'עריכת דרשן' : 'דרשן חדש'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="שם"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="קוד PIN"
              value={form.pin}
              onChange={(e) => setForm((f) => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 8) }))}
              fullWidth
              required
              helperText="4–8 ספרות"
            />
            <TextField
              select
              label="ערוץ שמיעה"
              value={form.channel}
              onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}
              fullWidth
            >
              {recorderChannels.map((ch) => (
                <MenuItem key={ch.id} value={ch.id}>{ch.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="הערות"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                />
              )}
              label="פעיל"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>ביטול</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'שמירה'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const Content = () => {
  const [tab, setTab] = useState(0);

  const lessonTabs = IS_MISHTAMERET
    ? contentLessonChannels().flatMap((ch) => {
      const tab = {
        key: ch.id,
        label: ch.label,
        render: () => (
          <DrashasTab
            channel={ch.id}
            title={ch.label}
            subtitle={`ערוץ תוכן לשלוחה ${ch.ivrPath} בטלפון (שלוחה 77). אם מוגדר דרשן לערוץ — השמיעה מתיקיית playfile.`}
            itemLabel="שיעור"
          />
        ),
      };
      if (ch.id === 'shinun_special') {
        return [
          tab,
          {
            key: 'shinun_magash',
            label: 'מג״ש בחצר קדשינו',
            render: () => <ShinunMagashTab />,
          },
        ];
      }
      return [tab];
    })
    : [{
      key: 'drasha',
      label: 'דרשות',
      render: () => <DrashasTab />,
    }];

  const tabs = [
    { key: 'messages', label: 'הודעות', render: () => <MessagesTab /> },
    ...(IS_MISHTAMERET
      ? [
        { key: 'system-messages', label: 'הודעות למערכת', render: () => <SystemMessagesTab /> },
        { key: 'recorders', label: 'דרשנים (הקלטה)', render: () => <LessonRecordersTab /> },
      ]
      : []),
    ...lessonTabs,
  ];

  const safeTab = Math.min(tab, tabs.length - 1);

  return (
    <Box>
      <AdminPageHeader
        title="תוכן"
        sub={tabs[safeTab]?.label}
        legacy={<Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>תוכן</Typography>}
      />
      <Tabs
        value={safeTab}
        onChange={(_e, v) => setTab(v)}
        sx={{ mb: 2 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs.map((t) => (
          <Tab key={t.key} label={t.label} />
        ))}
      </Tabs>
      {tabs[safeTab]?.render()}
    </Box>
  );
};

export default Content;
